import { useForm } from "react-hook-form";
import { Text } from "styles/utility-components";

import CustomerDocumentSelector from "components/form_components/CustomerDocumentSelector";
import Modal from "components/ui/modal/Modal";
import toast from "components/ui/toast/Toast";
import { UpdateCustomerDocumentRequest } from "lib/generated/app-service-gql/graphql";
import { getUserFacingMessage } from "lib/graphqlErrors";
import { CustomerDocumentDetails } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useUpdateCustomerDocument } from "lib/hooks/api/customer_documents/useUpdateCustomerDocument";
import { CustomerDocumentTypeFilter } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";

type Props = {
  selectedDocuments: CustomerDocumentDetails[];
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSuccess: () => void;
};
export function MoveCustomerDocumentModal({
  selectedDocuments,
  isOpen,
  onOpenChange,
  onSuccess,
}: Props) {
  const { control } = useForm<{ selectedSources: string[] }>({
    mode: "onSubmit",
    defaultValues: {
      selectedSources: [],
    },
  });

  const { mutate: updateDocument } = useUpdateCustomerDocument({
    onSuccess: async (data, variables) => {
      const updatedDocuments = data.updateCustomerDocument;

      // Will only be moving to one folder, so this is safe
      const movedToFolder = variables.request.updates[0].uiPath ?? "Root folder";

      if (updatedDocuments.length === 1) {
        toast.success(`Moved to ${movedToFolder}`);
      } else {
        const numberOfFolders = updatedDocuments.filter((doc) => doc.isFolder).length;
        const numberOfFiles = updatedDocuments.length - numberOfFolders;
        const folderText = numberOfFolders > 0 ? pluralise(numberOfFolders, "folder") : "";
        const fileText = numberOfFiles > 0 ? pluralise(numberOfFiles, "file") : "";

        let message = "";
        if (folderText && fileText) {
          message = `Moved ${folderText} and ${fileText} to ${movedToFolder}`;
        } else if (folderText || fileText) {
          message = `Moved ${folderText || fileText} to ${movedToFolder}`;
        }

        toast.success(message);
      }

      onOpenChange(false); // Close modal on success

      // Remove deleted documents from selection, if they were selected
      onSuccess();
    },
    onError: (err) => toast.error(getUserFacingMessage(err) ?? `Failed to move documents`),
  });

  return (
    <Modal
      open={isOpen}
      showCloseButton={false}
      onOpenChange={onOpenChange}
      padding={0}
      aria-label="Move modal"
    >
      <CustomerDocumentSelector
        title={
          <Text h2>
            {selectedDocuments.length > 1
              ? `Move ${selectedDocuments.length} items`
              : `Move "${selectedDocuments[0].fileName}"`}
          </Text>
        }
        documentType={CustomerDocumentTypeFilter.ContentLibrary}
        control={control}
        name="selectedSources"
        checkable={false}
        onlyFetchFolders
        showFlatSearch
        onSelect={(docs) => {
          const uiPath = docs[0].uiPath;

          const updates: UpdateCustomerDocumentRequest["updates"] = selectedDocuments.map(
            (doc) => ({
              id: doc.id,
              uiPath,
              previousUiPath: doc.uiPath,
              isFolder: doc.isFolder,
            }),
          );

          updateDocument({ request: { updates } });
        }}
        excludeDocuments={selectedDocuments.map((doc) => doc.id)}
      />
    </Modal>
  );
}
